import type { TableRecord } from '@powerportalspro/core';
import { PowerPortalsProProvider, RecordContext } from '@powerportalspro/react';
import {
  FluentDialogProvider,
  FluentOverlayProvider,
  LocalizationBoundary,
  officeColorValueToAccentName,
  PowerPortalsProThemeProvider,
  ThemeMode,
  useTheme,
} from '@powerportalspro/react-fluent';
import { useEffect, type ComponentType, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';

/**
 * Reads the current light/dark mode the Blazor host has applied via its
 * `<fluent-design-theme>` element (rendered inside `<loading-theme>` in
 * App.razor). Returns `null` when the element isn't present — outside the
 * docs site, e.g. running these samples in isolation. Caller falls back
 * to PowerPortalsProThemeProvider's own default.
 */
function readBlazorThemeMode(): ThemeMode | null {
  if (typeof document === 'undefined') return null;
  // Authoritative + startup-reliable: FluentDesignTheme.razor.js writes the
  // resolved light/dark mode to `document.body.dataset.theme`, and the Blazor
  // host server-renders it from the `ppp-theme` cookie — so it's in the initial
  // HTML before any JS runs. (The <fluent-design-theme> element's `mode`
  // attribute is populated client-side and can lag this sample's first sync,
  // which would leave it on PowerPortalsProThemeProvider's System default → the
  // OS preference → dark under a light site on a dark-OS machine.)
  const bodyTheme = document.body?.dataset.theme;
  if (bodyTheme === 'dark') return ThemeMode.Dark;
  if (bodyTheme === 'light') return ThemeMode.Light;
  // Fallback: the design-theme element's mode attribute.
  const mode = document.querySelector('fluent-design-theme')?.getAttribute('mode');
  if (mode?.includes('dark')) return ThemeMode.Dark;
  if (mode?.includes('light')) return ThemeMode.Light;
  return null;
}

/**
 * Reads the Blazor host's active accent and resolves it to a built-in
 * React accent name (`'default'`, `'excel'`, `'word'`, etc.) that the
 * surrounding `<PowerPortalsProThemeProvider>` can apply via
 * `setAccentColor`. The resolution (raw hex from the `CustomColor` path,
 * or a PascalCase `OfficeColor` enum name from the common path) is done
 * by the framework's `officeColorValueToAccentName`.
 *
 * Source chain (first hit wins):
 *   1. `primary-color` / `accent-base-color` / `custom-color` attribute
 *      on `<loading-theme > fluent-design-theme>`. The value may be a
 *      raw hex (if the consumer set `CustomColor`) or an `OfficeColor`
 *      enum NAME like `"Access"` / `"Excel"` (FluentUI Blazor's more
 *      common code path persists the enum name, not the hex).
 *   2. `localStorage["theme"].primaryColor` — same value as #1, saved
 *      by FluentDesignTheme's themeStorage between sessions.
 *
 * Returns `null` when nothing matches. The bridge silently skips
 * unrecognized values rather than throwing.
 */
function readBlazorAccentName(): string | null {
  if (typeof document === 'undefined') return null;
  return officeColorValueToAccentName(readBlazorAccentRawValue());
}

function readBlazorAccentRawValue(): string | null {
  // Authoritative + startup-reliable: FAST publishes the active accent hex on
  // document.documentElement (--accent-base-color), and FluentDesignTheme
  // persists the accent name to localStorage["theme"].primaryColor. Both are
  // present at startup — before the <fluent-design-theme> element's
  // primary-color attribute settles (the lag that left the sample accent on the
  // provider's 'default' until a manual color change).
  if (typeof document !== 'undefined') {
    try {
      const accentHex = getComputedStyle(document.documentElement)
        .getPropertyValue('--accent-base-color')
        .trim();
      if (accentHex) return accentHex;
    } catch {
      // getComputedStyle can throw on a detached document — fall through.
    }
  }
  try {
    const raw = window.localStorage.getItem('theme');
    if (raw) {
      const parsed = JSON.parse(raw) as { primaryColor?: unknown };
      if (typeof parsed.primaryColor === 'string' && parsed.primaryColor.length > 0) {
        return parsed.primaryColor;
      }
    }
  } catch {
    // localStorage unavailable / invalid JSON — fall through.
  }
  // Fallback: the live design-theme element's attributes.
  const designTheme = document.querySelector('fluent-design-theme');
  if (designTheme) {
    for (const attr of ['primary-color', 'accent-base-color', 'custom-color']) {
      const v = designTheme.getAttribute(attr);
      if (v) return v;
    }
  }
  return null;
}

/**
 * Internal bridge component: pushes the Blazor host's theme mode AND
 * accent color into the surrounding `<PowerPortalsProThemeProvider>` on
 * mount and whenever the Blazor side updates either. Same `onchange`
 * event the docs site's highlight-extensions script subscribes to,
 * just consumed from React.
 *
 * Renders nothing — only the side-effects of `useTheme().setMode(...)`
 * and `setAccentColor(...)` matter. The provider's `storageKey` is
 * sample-scoped so these writes don't clobber the user's actual
 * React-side theme preference (consumer React apps write to
 * `ppp-theme-mode` / `ppp-theme-accent`; samples write to separate keys).
 *
 * Accent matching goes through `officeColorValueToAccentName`, which
 * resolves the Blazor host's persisted accent (enum name or raw hex) to
 * a built-in React accent name; the provider's `setAccentColor` is a
 * no-op when the value doesn't resolve, so unknown accents leave the
 * sample on the default rather than breaking.
 */
function BlazorThemeSync(): null {
  const { setMode, setAccentColor } = useTheme();

  useEffect(() => {
    const sync = () => {
      const mode = readBlazorThemeMode();
      if (mode !== null) setMode(mode);
      const accent = readBlazorAccentName();
      if (accent !== null) setAccentColor(accent);
    };

    // Initial sync from the authoritative, already-present startup values.
    sync();

    // Re-sync on the signals the Blazor host mutates when the theme changes:
    // `body[data-theme]` for light/dark (the same attribute the framework's own
    // ThemeProvider observes) and documentElement's inline style for FAST's
    // republished `--accent-base-color` when the accent changes. Observing these
    // — rather than a specific <fluent-design-theme> element — keeps the sync
    // working regardless of where the host renders that element, and doesn't
    // depend on it existing at mount time.
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => observer.disconnect();
  }, [setMode, setAccentColor]);

  return null;
}

/**
 * Optional wrappers the docs envelope mounts around a sample. Lifts
 * boilerplate (a `<RecordContext>` for editor samples, etc.) OUT of the
 * sample file so the source tab shows just the meaningful bit — mirrors
 * how Blazor demo `.razor` files contain only `<TextEdit ColumnName=".." />`
 * while the surrounding page (`TextEditDemoPage.razor`) supplies the
 * `<RecordContext>`. Each option toggles its wrapper on; sample files stay
 * focused on the component they're documenting.
 */
export interface SampleConfig {
  /** Wraps the sample in a `<RecordContext>` so column-bound editors have
   *  somewhere to bind. Omit when the sample manages its own RecordContext
   *  (or doesn't need one — e.g. layout-only samples). */
  recordContext?: {
    /** Table logical name (e.g. `"ppp_democustomtable"`). */
    table: string;
    /** Optional record id. Omit for create-mode (fresh record, no fetch). */
    id?: string;
    /** When `true`, skip the server fetch and seed RecordContext with a
     *  stub `{ tableName, id }` record via the `loadedRecord` prop. Mirrors
     *  the Blazor demo pattern of passing a hand-constructed `TableRecord`
     *  directly into `<RecordContext Record="...">` for cases where the
     *  descendants only need the parent reference (table + id), not the
     *  loaded column values — e.g. SubGrid samples that use the parent
     *  as a relationship filter key. Without this, a failing or slow
     *  fetch would leave the sample area blank because descendants gate
     *  on `record !== undefined`. Has no effect without `id`. */
    skipFetch?: boolean;
  };
}

/**
 * Registers a React component as a browser custom element so it can be
 * mounted from a non-React host page (the Blazor demo site) by emitting
 * `<tag-name></tag-name>` markup. The host loads `samples.js` once; after
 * that any number of these tags on the page just work.
 *
 * Each instance constructs its own provider envelope — FluentUI v9 theme,
 * PowerPortalsPro client/auth/localization, dialog + overlay portals,
 * plus any config-driven wrappers (`<RecordContext>`, ...) — so the sample
 * file stays minimal and the surrounding host page needs nothing
 * React-specific.
 *
 * Light-DOM mount (no `attachShadow`): FluentUI v9 injects styles into
 * `document.head` via Griffel, and a shadow root would isolate those
 * styles away from the rendered subtree. Light DOM is the path of least
 * resistance for FluentUI 9 + custom elements; downside is the parent
 * page's CSS can reach the sample, which is fine for these docs.
 */
export function defineSampleElement(
  tagName: string,
  Component: ComponentType,
  config?: SampleConfig,
): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get(tagName)) return;

  class SampleElement extends HTMLElement {
    private root: Root | null = null;

    connectedCallback(): void {
      // Custom elements default to `display: inline` with `min-width: auto`.
      // Inside a flex/grid parent (e.g. the demo site's
      // `.ppp-section-column-content-stack`, which is `display: flex`), that
      // means the host element's flex `min-width` resolves to its CONTENT's
      // min-content size — including the intrinsic width of any wide tables
      // inside the React tree — and the host blows past its parent's
      // bounds. `display: block; min-width: 0; max-width: 100%` is the
      // matching pair to `.ppp-section`'s own defensive sizing (see
      // Section.razor.css): block-level so percentage children resolve
      // sanely, `min-width: 0` so the flex algorithm doesn't widen us to
      // content size, and `max-width: 100%` belt-and-braces in case any
      // ancestor sets `width` rather than relying on the flex stretch.
      this.style.display = 'block';
      this.style.minWidth = '0';
      this.style.maxWidth = '100%';
      this.root = createRoot(this);
      let content: ReactNode = <Component />;
      if (config?.recordContext) {
        // Spread `id` only when provided — passing `id={undefined}`
        // explicitly to RecordContext is the create-mode signal, but the
        // type accepts `string | undefined` and the framework treats
        // omitted-vs-undefined the same. Keeping the spread tidy.
        //
        // When `skipFetch` is set (and `id` is present), seed the
        // RecordContext with a stub `loadedRecord` so the server fetch is
        // bypassed and descendants see `record` immediately on first
        // render. Mirrors the Blazor demo pattern for SubGrid-style
        // samples where the parent record is only used as a relationship
        // filter key, not for its column values.
        const stubLoaded: TableRecord | undefined =
          config.recordContext.skipFetch && config.recordContext.id !== undefined
            ? {
                tableName: config.recordContext.table,
                id: config.recordContext.id,
                properties: {},
              }
            : undefined;
        content = (
          <RecordContext
            table={config.recordContext.table}
            {...(config.recordContext.id !== undefined && { id: config.recordContext.id })}
            {...(stubLoaded !== undefined && { loadedRecord: stubLoaded })}
          >
            {content}
          </RecordContext>
        );
      }
      // Gate render on the framework's default localization bundle so
      // samples using `t(...)` paint with translated strings on first
      // commit instead of flashing raw keys for the ~50-100ms it takes
      // the bundle to resolve. The boundary's default fallback is a
      // centered Fluent Spinner; on a cache-hot navigation the bundle
      // is already settled and the boundary mounts children immediately
      // with no spinner shown.
      //
      // `storageKey="ppp-sample-theme"` scopes the theme provider's
      // localStorage to a sample-only slot. `<BlazorThemeSync>` writes
      // into the provider via `setMode` / `setAccentColor` on every
      // Blazor host theme change; without the dedicated key those
      // writes would clobber the user's real React-app theme preference
      // (`ppp-theme-mode` / `ppp-theme-accent`).
      //
      // The full FluentUI Blazor `OfficeColor` palette ships as built-in
      // accents on `<PowerPortalsProThemeProvider>`, so `BlazorThemeSync`
      // can `setAccentColor(name)` for any Office brand the host applies
      // without this envelope registering `customAccents`.
      this.root.render(
        <PowerPortalsProThemeProvider
          storageKey="ppp-sample-theme"
          // Paint the FluentProvider's surface with the Blazor host's
          // `--neutral-layer-1` instead of letting Fluent v9's default
          // `colorNeutralBackground1` (pure #ffffff) win. Fluent v9 and
          // the FAST host's token systems both pick "light surface" tokens
          // that agree in dark mode but diverge slightly in light — Fluent
          // v9 hard-codes pure white while FAST's layer-1 picks a subtly
          // tinted off-white tuned to the active accent. The mismatch
          // shows up as a visible white rectangle around React samples
          // sitting inside a Section's content surface. Overriding here
          // (in the docs envelope only) keeps the React/Blazor seam
          // invisible without affecting consumer apps that mount
          // `<PowerPortalsProThemeProvider>` directly. The `--neutral-layer-1`
          // CSS variable is defined globally by the Blazor host page, so
          // it resolves correctly here even though we're inside React.
          style={{ backgroundColor: 'var(--neutral-layer-1)' }}
        >
          <BlazorThemeSync />
          <PowerPortalsProProvider>
            <LocalizationBoundary>
              <FluentDialogProvider>
                <FluentOverlayProvider>{content}</FluentOverlayProvider>
              </FluentDialogProvider>
            </LocalizationBoundary>
          </PowerPortalsProProvider>
        </PowerPortalsProThemeProvider>,
      );
    }

    disconnectedCallback(): void {
      this.root?.unmount();
      this.root = null;
    }
  }

  customElements.define(tagName, SampleElement);
}
